import {
  AxiosError,
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig
} from 'axios';
import { store } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { setIsOffline } from '../../store/slices/appStatusSlice';
import { showToast } from '../../store/slices/toastSlice';
import { authService } from '../AuthService';
import { getErrorMessage } from '../../utils/errorMessages';
import { PATHS } from '../../constants/Paths.constants';
import { isOidcAuth } from '../../config/featureFlags';
import { isApiErrorCode, normalizeApiError } from '../../utils/apiError';
import { isLogoutInProgress } from '../../utils/Auth.util';
// Import the shared UserManager instance — same object used by OidcAuthProvider
// so there is only ever one UserManager alive at a time.
import { userManager } from '../../components/OidcAuthProvider';

/**
 * Trigger a silent OIDC token refresh via the shared UserManager.
 * Returns the new access_token on success, or null on failure.
 */
const silentOidcRefresh = async (): Promise<string | null> => {
  try {
    const user = await userManager.signinSilent();
    return user?.access_token ?? null;
  } catch {
    return null;
  }
};

const startOidcRelogin = () => {
  const returnTo = `${window.location.pathname}${window.location.search}`;

  void userManager.signinRedirect({ state: { returnTo } }).catch(() => {
    window.location.href = PATHS.LOGIN;
  });
};

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: any = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const getRetryAfterSeconds = (headers: any): number | null => {
  if (!headers) return null;

  const retryAfterRaw =
    (typeof headers.get === 'function' && headers.get('retry-after')) ||
    headers['retry-after'] ||
    headers['Retry-After'];

  if (!retryAfterRaw) return null;

  const retryAfterSeconds = Number(retryAfterRaw);
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) {
    return retryAfterSeconds;
  }

  return null;
};

const formatErrorMessage = (message: unknown): string | undefined => {
  if (Array.isArray(message)) {
    return message
      .map((item) => String(item).trim())
      .filter(Boolean)
      .map((item) => (/[.!?]$/.test(item) ? item : `${item}.`))
      .join(' ');
  }

  if (typeof message === 'string') {
    return message;
  }

  return undefined;
};

export const createAuthInterceptor = (axiosInstance: AxiosInstance) => ({
  response: (response: AxiosResponse) => {
    store.dispatch(setIsOffline(false));
    return response;
  },

  responseError: async (error: AxiosError) => {
    const originalRequest = (error.config ||
      {}) as InternalAxiosRequestConfig & {
      _retry?: boolean;
      skipErrorToast?: boolean;
      skipAccessDeniedRedirect?: boolean;
    };

    if (isOidcAuth() && isLogoutInProgress()) {
      return Promise.reject(error);
    }

    const statusCode = error.response?.status;
    const errorMessage = (error.response?.data as any)?.message;
    const isRefreshEndpoint = originalRequest.url?.includes('/auth/refresh');
    const isNetworkError =
      !error.response ||
      error.code === 'ERR_NETWORK' ||
      (typeof navigator !== 'undefined' && !navigator.onLine);

    // Handle network/offline errors (no HTTP response).
    if (isNetworkError) {
      store.dispatch(setIsOffline(true));
    }

    // Show toast for errors (except 401 which is handled separately)
    if (statusCode && statusCode !== 401 && !originalRequest?.skipErrorToast) {
      let message =
        formatErrorMessage(errorMessage) || getErrorMessage(statusCode);

      if (statusCode === 429) {
        const retryAfterSeconds = getRetryAfterSeconds(error.response?.headers);
        if (retryAfterSeconds) {
          message = `Too many requests. Please try again in ${retryAfterSeconds} seconds.`;
        }
      }

      store.dispatch(showToast({ message, type: 'error' }));
    }

    // Scope-related 403s are not "you may not be here" — they mean this one
    // request could not be scoped to an organization. A superadmin gets
    // NO_ORGANIZATION_CONTEXT from every per-organization write, so redirecting
    // would throw them out of the app for using a disabled control. The toast
    // above already reported it; let the caller handle it inline.
    const isScopedRequestError = isApiErrorCode(
      error,
      'NO_ORGANIZATION_CONTEXT',
      'CROSS_ORG_ACCESS_DENIED'
    );

    if (
      statusCode === 403 &&
      !isScopedRequestError &&
      !originalRequest?.skipAccessDeniedRedirect
    ) {
      const currentPath = window.location.pathname;
      if (currentPath !== PATHS.ACCESS_DENIED && currentPath !== PATHS.LOGIN) {
        window.location.href = PATHS.ACCESS_DENIED;
      }
    }

    // Should set 410 instead of 400
    // Handle 400 errors - redirect to expired link page
    // if (error.response?.status === 400) {
    //   window.location.href = PATHS.EXPIRED_LINK;
    //   return Promise.reject(error);
    // }

    // Terminal 401s: the token itself is wrong, so refreshing or re-authenticating
    // returns the identical token and loops. Must be checked before the refresh
    // branch below.
    if (
      statusCode === 401 &&
      isApiErrorCode(
        error,
        'PLATFORM_ROLE_CLAIM_INVALID',
        'ORG_CONTEXT_MISSING'
      )
    ) {
      processQueue(error, null);

      if (window.location.pathname !== PATHS.NO_ORGANIZATION) {
        window.location.href = PATHS.NO_ORGANIZATION;
      }

      return Promise.reject(normalizeApiError(error));
    }

    // If refresh endpoint itself returns 401, force logout
    if (statusCode === 401 && isRefreshEndpoint) {
      processQueue(error, null);
      store.dispatch(logout());

      if (isOidcAuth()) {
        startOidcRelogin();
      } else {
        window.location.href = '/login';
      }

      return Promise.reject(normalizeApiError(error));
    }

    // Skip token refresh for auth endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes('/auth/login') ||
      originalRequest.url?.includes('/auth/signup') ||
      isRefreshEndpoint;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(normalizeApiError(err));
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        if (isOidcAuth()) {
          // OIDC path: use silent renew to get a fresh access token
          const newToken = await silentOidcRefresh();
          if (newToken) {
            processQueue(null, newToken);
            // The Authorization header is injected by ApiBase per-request,
            // so we just retry — getOidcUser() will return the refreshed token.
            return axiosInstance(originalRequest);
          } else {
            processQueue(error, null);
            store.dispatch(logout());
            startOidcRelogin();
            return Promise.reject(normalizeApiError(error));
          }
        } else {
          // Local auth path: use the existing cookie-based refresh endpoint
          const refreshSuccess = await authService.refreshToken();

          if (refreshSuccess) {
            processQueue(null, null);
            return axiosInstance(originalRequest);
          } else {
            processQueue(error, null);
            store.dispatch(logout());
            window.location.href = '/login';
            return Promise.reject(normalizeApiError(error));
          }
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        store.dispatch(logout());

        if (isOidcAuth()) {
          startOidcRelogin();
        } else {
          window.location.href = '/login';
        }

        return Promise.reject(normalizeApiError(refreshError));
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(normalizeApiError(error));
  }
});
